import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, ChatTextField, MessageType, Text, ThreeDotsIcon, LiveChatMessage, UserAvatar, RPopover, } from "@ting/ting-ui-components";
import { ChevronDownFilledIcon, ChevronDownOutlinedIcon, DeliveredOutlinedIcon, EmojiOutlinedIcon, } from "@ting/ting-ui-components/dist/icons";
import { useEffect, useRef, useState } from "react";
import { nanoid } from "nanoid";
import { classNameFactory } from "@src/utils/dom";
import { smoothScroll } from "@src/utils/helpers/smooth-scroll";
import { useDebounce } from "@src/hooks/useDebounce";
import { useSubscribe } from "@src/hooks/useSubscribe";
import { selectUser } from "@src/selectors";
import configs from "@src/configs";
import { EmojiPicker } from "@src/components/Chat/EmojiPicker";
import { LiveChatWebTorrentUtil } from "@src/services/WebtorrentService";
import { selectLiveChatMessages } from "@src/selectors/live-chat";
import { addLiveChatMessage } from "@src/controller/live-chat/chat";
import "./ChatFrame.scss";
const cn = classNameFactory("live-chat-frame");
export const LiveChatFrame = () => {
    const user = useSubscribe(selectUser());
    const messages = useSubscribe(selectLiveChatMessages());
    const [isTouched, setIsTouched] = useState(false);
    const messagesListRef = useRef(null);
    const scrollDownRef = useRef(0);
    const [messageText, setMessageText] = useState("");
    const [postingAccount, setPostingAccount] = useState(user?.account);
    const [showPicker, setShowPicker] = useState(false);
    const togglePicker = () => setShowPicker(!showPicker);
    const adjustScroll = () => {
        const messagesList = messagesListRef.current;
        if (!messagesList) {
            return;
        }
        const { clientHeight, scrollHeight } = messagesList;
        const scrollDown = scrollDownRef.current;
        const scrollTop = isTouched ? Math.max(0, scrollDown - clientHeight) : scrollHeight - clientHeight;
        smoothScroll(messagesList, { top: scrollTop, left: 0 });
    };
    useEffect(adjustScroll, [messages, isTouched]);
    const handleScroll = useDebounce(() => {
        const messagesList = messagesListRef.current;
        if (!messagesList) {
            return;
        }
        const lastScrollDown = scrollDownRef.current;
        const { scrollTop, clientHeight, scrollHeight } = messagesList;
        const scrollDown = scrollTop + clientHeight;
        scrollDownRef.current = scrollDown;
        if (!isTouched && scrollDown < lastScrollDown) {
            setIsTouched(true);
        }
        else if (isTouched && scrollHeight - scrollDown < 10) {
            setIsTouched(false);
        }
    }, 300);
    useEffect(() => {
        const messagesList = messagesListRef.current;
        messagesList.addEventListener("scroll", handleScroll);
        return () => {
            messagesList.removeEventListener("scroll", handleScroll);
        };
    }, [handleScroll]);
    function sendMessage(msg) {
        const chatUser = {
            displayName: postingAccount?.displayName,
            userName: postingAccount.name,
            avatarUrl: postingAccount.avatar && `${postingAccount.avatar?.path}`,
            corepassId: user.corepassId,
        };
        const newMessage = msg || {
            type: MessageType.CHAT,
            id: nanoid(),
            content: messageText,
            time: new Date().toISOString(),
            from: chatUser,
            reactions: [],
            viewer: chatUser,
        };
        if ((newMessage.type === MessageType.CHAT && messageText.length < 0) || messageText.trim().length < 0) {
            return;
        }
        LiveChatWebTorrentUtil.Instance.sendNewLiveChatMessage(newMessage);
        addLiveChatMessage(newMessage);
        setMessageText("");
    }
    const handleNewMessageChange = (e) => {
        const text = e.currentTarget.value;
        setMessageText(text);
    };
    const handleKeyDown = (event) => {
        if (event.key === "Enter" || event.keyCode === 13) {
            event.preventDefault();
            sendMessage();
        }
    };
    return (_jsxs("div", { className: cn(""), children: [_jsxs("header", { className: cn("header"), children: [_jsx(Text, { size: "p1", fontWeight: "semiBold", children: "Live Chat" }), _jsx(Button, { v2: true, icon: _jsx(ThreeDotsIcon, { width: 18, height: 18 }), size: "small", type: "neutral", variant: "primary" })] }), _jsxs("div", { className: cn("messages-container"), children: [_jsx("ul", { className: cn("messages-list"), ref: messagesListRef, children: messages.map((msg, idx) => (_jsx(LiveChatMessage, { message: { ...msg, type: "normal" }, showAvatar: false }, idx))) }), _jsx(Button, { v2: true, round: true, size: "small", startIcon: _jsx(ChevronDownFilledIcon, { width: 20 }), type: "neutral-inverted", className: cn("scroll-down-btn", { hide: !isTouched }), onClick: () => setIsTouched(false) })] }), _jsxs("div", { className: cn("footer"), children: [_jsxs("div", { className: cn("message-options"), children: [user && (_jsxs("div", { className: cn("posting-as"), children: [_jsx(Text, { size: "body-medium", children: "Posting as:" }), _jsx(RPopover, { zIndex: 1000, offset: { x: 0, y: 10 }, placement: "bottom-start", trigger: _jsxs("div", { className: cn("posting-as-btn"), children: [_jsx(UserAvatar, { image: postingAccount.avatar && `${configs.app.fileServer}${postingAccount.avatar.path}`, size: "xxs", userName: postingAccount.displayName, alt: postingAccount.displayName }), _jsx(Button, { v2: true, startIcon: _jsx(ChevronDownOutlinedIcon, { height: "15", width: "15" }), type: "neutral", variant: "ghost", size: "small", htmlType: "button", round: true })] }), content: _jsxs("div", { className: cn("posting-as-popover"), children: [_jsx(Text, { type: "sub", size: "tiny", className: cn("posting-as-heading"), children: "Profiles" }), _jsxs(Button, { v2: true, variant: "ghost", className: cn("posting-as-option"), onClick: () => setPostingAccount(user.account), role: "button", endIcon: user.account.name === postingAccount.name && _jsx(DeliveredOutlinedIcon, { width: 15, height: 15 }), children: [_jsx(UserAvatar, { image: user?.account.avatar && `${configs.app.fileServer}${user.account.avatar.path}`, size: "sm", userName: user.account.displayName, alt: user.account.displayName }), _jsx(Text, { className: "account-name-text", fontWeight: "semiBold", type: "main", children: user.account.displayName })] }), _jsx(Text, { type: "sub", size: "tiny", className: cn("posting-as-heading"), children: "Channels" }), user?.videoChannels.map(channel => (_jsxs(Button, { v2: true, variant: "ghost", className: cn("posting-as-option"), onClick: () => setPostingAccount(channel), endIcon: channel.name === postingAccount.name && _jsx(DeliveredOutlinedIcon, { width: 15, height: 15 }), children: [_jsx(UserAvatar, { className: "user-avatar", image: channel.avatar && `${configs.app.fileServer}${channel.avatar.path}`, size: "sm", userName: channel.displayName, alt: channel.displayName }), _jsx(Text, { fontWeight: "semiBold", type: "main", children: channel.displayName })] }, channel.id)))] }) })] })), _jsx(Button, { v2: true, icon: _jsx(EmojiOutlinedIcon, { width: 20 }), size: "small", type: "neutral", variant: "primary", onClick: () => setTimeout(() => {
                                    setShowPicker(true);
                                }) })] }), _jsx(ChatTextField, { value: messageText, placeholder: "Send message...", disabled: false, onKeyPress: handleKeyDown, onChange: handleNewMessageChange, onSend: () => sendMessage() }), showPicker && (_jsx(EmojiPicker, { handlePick: emoji => setMessageText(messageText + emoji), togglePicker: togglePicker }))] })] }));
};
